import { Injectable } from '@angular/core';
import { JhiLanguageService } from '../core-utils';
import { SERVER_API_URL } from '../../app.constants';
import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { from, Observable, throwError } from 'rxjs';

import { LoginPageDetails } from './login.model';
import { catchError, map, switchMap } from 'rxjs/operators';
import { UserProfileModel } from '../../entities/user-profile/model/user-profile.model';
import { License } from '../model/license.model';
import { TranslateService } from '@ngx-translate/core';
import { SkipErrorInterceptor } from '../../blocks/interceptor/error.interceptor';
import { SkipAuthExpiredInterceptor } from '../../blocks/interceptor/auth-expired.interceptor';

@Injectable({ providedIn: 'root' })
export class LoginService {

    private isLoggingOut = false;

    constructor(
        private http: HttpClient,
        private jhiLanguageService: JhiLanguageService,
        private translateService: TranslateService,
    ) {
    }

    forceLogout() {
        if (this.isLoggingOut) {
            return; // Prevent multiple executions
        }

        this.isLoggingOut = true;


        alert('Breeding Management System needs to authenticate you again. Redirecting to login page.');
        this.logout();
    }

    async logout() {
        // Perform a sequential logout process:
        // 1. Logout from Fieldbook
        // 2. Logout from GDMS
        // 3. Redirect to Workbench logout
        // This order ensures that sessions and cookies are properly reset
        // before the final logout in Workbench, preventing potential
        // authentication conflicts or lingering sessions.
        try {
            await this.logoutFieldbook().toPromise();
        } catch (error) {
            console.error(error);
        } finally {
            // Reset the flag when logout completes or fails
            this.isLoggingOut = false;
        }

        window.top.location.href = '/ibpworkbench/logout';

    }

    validateToken(skipAuthExpiredIntercept = false) {
        const context = skipAuthExpiredIntercept
            ? new HttpContext().set(SkipAuthExpiredInterceptor, true).set(SkipErrorInterceptor, true)
            : new HttpContext();

        const url = SERVER_API_URL + 'validateToken';
        return this.http.get<void>(url, { context });
    }

    getLoginPageDetails(): Observable<LoginPageDetails> {
        const url = SERVER_API_URL + 'auth/login';
        return this.http.get<LoginPageDetails>(url,
            { observe: 'response' }).pipe(
            switchMap((res: HttpResponse<LoginPageDetails>) => {
                const loginDetails = res.body;
                return from(this.checkImageUrl(loginDetails.instituteLogoPath)).pipe(
                    map((logoExists) => {
                        loginDetails.existsInstituteLogo = logoExists;
                        return loginDetails;
                    })
                );
            }));
    }

    validateLogin(request: any): Observable<any> {
        const url = SERVER_API_URL + 'auth/validateLogin';
        const httpContext = new HttpContext().set(SkipErrorInterceptor, true);
        return this.http.post<any>(url, request, { observe: 'response', context: httpContext }).pipe(map((res: HttpResponse<any>) => res.body));
    }

    authorizeWorkbench(model: UserProfileModel): Observable<any> {
        const params = new HttpParams()
            .set('username', model.username)
            .set('password', model.password)
            .set('remember-me', model.rememberMe);
        // Simulate form submit to workbenchLogin. This will initialize Spring Security authentication for Workbench
        const url = '/ibpworkbench/workbenchLogin';
        return this.http.post<any>(url, params, {
            headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded'),
            observe: 'response'
        }).pipe(map((res: HttpResponse<any>) => res.body));
    }

    getBVDesignLicense(): Observable<any> {
        return this.http.get<any>(SERVER_API_URL + 'breeding-view-licenses',
            { observe: 'response' }).pipe(map((res: HttpResponse<License[]>) => res.body));
    }

    createOTP(request: any): Observable<any> {
        // use workbench endpoint for otp creation
        const url = SERVER_API_URL + 'auth/otp/create';
        const httpContext = new HttpContext().set(SkipErrorInterceptor, true);
        return this.http.post<any>(url, request, {
            headers: new HttpHeaders().set('Content-Type', 'application/json'),
            observe: 'response', context: httpContext
        }).pipe(map((res: HttpResponse<any>) => res.body));
    }

    verifyOTP(request: any): Observable<any> {
        const url = SERVER_API_URL + 'auth/otp/verify';
        const httpContext = new HttpContext().set(SkipErrorInterceptor, true);
        return this.http.post<any>(url, request, { observe: 'response', context: httpContext }).pipe(map((res: HttpResponse<any>) => res.body));
    }

    sendResetEmail(request: any): Observable<any> {
        const url = SERVER_API_URL + 'auth/sendResetEmail';
        const httpContext = new HttpContext().set(SkipErrorInterceptor, true);
        return this.http.post<any>(url, request, { observe: 'response', context: httpContext }).pipe(map((res: HttpResponse<any>) => res.body));
    }

    getResetPageDetails(token: string): Observable<UserProfileModel> {
        const url = SERVER_API_URL + `auth/reset/${token}`;
        return this.http.get<UserProfileModel>(url,
            { observe: 'response' }).pipe(map((res: HttpResponse<UserProfileModel>) => res.body));
    }

    submitResetPassword(token: string, request: any): Observable<any> {
        const url = SERVER_API_URL + `auth/reset/${token}`;
        const httpContext = new HttpContext().set(SkipErrorInterceptor, true);
        return this.http.post<any>(url, request, { observe: 'response', context: httpContext }).pipe(map((res: HttpResponse<any>) => res.body));
    }

    logoutFieldbook(): Observable<any> {
        const httpContext = new HttpContext().set(SkipErrorInterceptor, true);
        return this.http.get('/Fieldbook/logout',
            { responseType: 'text', context: httpContext }).pipe(catchError(this.handleError));
    }

    renewFieldbookSession(): Observable<any> {
        const httpContext = new HttpContext().set(SkipErrorInterceptor, true);
        return this.http.get('/Fieldbook/renew-session', { responseType: 'text', context: httpContext }).pipe(catchError(this.handleError));
    }

    private handleError(error: HttpErrorResponse): Observable<never> {
        let errorMessage = 'An error occurred';
        if (error.error instanceof ErrorEvent) {
            // Client-side or network error
            errorMessage = `Error: ${error.error.message}`;
        } else {
            // Backend error
            errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
        }
        console.error(errorMessage);
        return throwError(() => new Error(errorMessage));
    }

    private checkImageUrl(url: string): Promise<boolean> {
        return new Promise((resolve) => {
            const img = new Image();
            img.src = url;
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
        });
    }

}
